import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Tabs, Tab, styled, useMediaQuery } from '@mui/material';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PaymentIcon from '@mui/icons-material/Payment';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlatformIntegrationsSection from './PlatformIntegrationsSection';
import PaymentIntegrationsSection from './PaymentIntegrationsSection';

type TabKey = 'platform' | 'payment';
const VALID_TABS: TabKey[] = ['platform', 'payment'];

const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  '@media (max-width: 1499px)': {
    padding: theme.spacing(2),
    height: 'auto',
    overflowY: 'visible',
  },
}));

const TabPanel: React.FC<{ value: TabKey; current: TabKey; children: React.ReactNode }> = ({
  value,
  current,
  children,
}) => {
  if (value !== current) return null;
  return <Box>{children}</Box>;
};

const IntegrationPage: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (() => {
    const q = (searchParams.get('tab') || '').toLowerCase();
    return VALID_TABS.includes(q as TabKey) ? (q as TabKey) : 'platform';
  })();

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  useEffect(() => {
    const q = (searchParams.get('tab') || '').toLowerCase();
    const next = VALID_TABS.includes(q as TabKey) ? (q as TabKey) : 'platform';
    if (next !== activeTab) setActiveTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: TabKey) => {
      setActiveTab(newValue);
      const next = new URLSearchParams(searchParams);
      next.set('tab', newValue);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return (
    <ScrollableContent>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <IntegrationInstructionsIcon
          sx={{ fontSize: isMobile ? 28 : 32, mr: 1, color: 'primary.main' }}
        />
        <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" fontWeight="bold">
          {t('integrationPage.title')}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            value="platform"
            icon={<StorefrontIcon />}
            iconPosition="start"
            label="Platform Entegrasyonları"
          />
          <Tab
            value="payment"
            icon={<PaymentIcon />}
            iconPosition="start"
            label="Ödeme Entegrasyonları"
          />
        </Tabs>
      </Box>

      <TabPanel value="platform" current={activeTab}>
        <PlatformIntegrationsSection />
      </TabPanel>

      <TabPanel value="payment" current={activeTab}>
        <PaymentIntegrationsSection />
      </TabPanel>
    </ScrollableContent>
  );
};

export default IntegrationPage;
